// The sound-effect banks and jukebox tracks, transcribed from the Unity
// scenes (plan 18). Pure data — no Web Audio, no DOM; the director maps
// events to banks and the engine turns URLs into sound.
//
// Sources of truth (GUIDs resolved via the .meta files, re-verified for this
// transcription rather than copied from the plan):
//   - `MainGame.unity:3413-3458` — the `SoundEffects` bank bindings
//   - `MainGame.unity:2334-2364` — the `JukeBox` clip/volume tables
//   - `MainGame.unity:3933-3960` — the seven `JukeBoxPlayCommand`s
//   - `MainMenu.unity:8552-8555` — the menu scene's boot-music command
//     (clip 5 = Summer.mp3, played once)
// The menu scene also binds a *smaller* SoundEffects copy (`MainMenu.unity:
// 8363-8400`): block sounds nulled, lady/fat death banks empty, and a
// FatBugHit whose GUID (7046c8b9...) resolves to no asset in the project — a
// dangling reference, so the menu's fat-bug-hit is silent there. The port
// uses the main game's bindings everywhere (one game, one binding); the
// menu-vs-game block-sound difference is reproduced by the director's
// in-game gate instead, mirroring the original's `gameStates == Running`
// guard around the block one-shots (`Cube.cs:1943`, `:1958`).
//
// Parcel resolves `new URL(<literal>, import.meta.url)` statically, so every
// URL is written out longhand — no helper, no loop (same constraint as
// `model-catalog.ts`). Every file referenced here is produced by
// `tools/extract_audio.py`, un-ignored in the root `.gitignore` and
// committed for CI (`tests/audio-catalog.test.ts` checks).

const bugSpawn1 = new URL("../../assets/audio/bug-spawn.mp3", import.meta.url).href;
const bugSpawn2 = new URL("../../assets/audio/bug-spawn2.mp3", import.meta.url).href;
const bugSpawn3 = new URL("../../assets/audio/bug-spawn3.mp3", import.meta.url).href;
const bugDie1 = new URL("../../assets/audio/bug-die1.mp3", import.meta.url).href;
const bugDie2 = new URL("../../assets/audio/bug-die2.mp3", import.meta.url).href;
const bugDie3 = new URL("../../assets/audio/bug-die3.mp3", import.meta.url).href;
const bugDie4 = new URL("../../assets/audio/bug-die4.mp3", import.meta.url).href;
const bugDie5 = new URL("../../assets/audio/bug-die5.mp3", import.meta.url).href;
const bugDie6 = new URL("../../assets/audio/bug-die6.mp3", import.meta.url).href;
const bugInfect6 = new URL("../../assets/audio/bug-infect6.mp3", import.meta.url).href;
const bugInfect7 = new URL("../../assets/audio/bug-infect7.mp3", import.meta.url).href;
const blockDown = new URL("../../assets/audio/block-down.mp3", import.meta.url).href;
const blockUp = new URL("../../assets/audio/block-up.mp3", import.meta.url).href;
const cubeRotates1 = new URL("../../assets/audio/cube-rotates-1.mp3", import.meta.url).href;
const cubeRotates2 = new URL("../../assets/audio/cube-rotates-2.mp3", import.meta.url).href;
const cubeRotates3 = new URL("../../assets/audio/cube-rotates-3.mp3", import.meta.url).href;
const powerUpSpawn = new URL("../../assets/audio/powerup-spawn.mp3", import.meta.url).href;
const powerUpBugKill = new URL("../../assets/audio/powerup-bug-kill.mp3", import.meta.url).href;
const powerUpBugKill2 = new URL("../../assets/audio/powerup-bug-kill-2.mp3", import.meta.url).href;
const powerUpPickPlayer = new URL("../../assets/audio/powerup-pick-player.mp3", import.meta.url).href;
const ladyBugPick = new URL("../../assets/audio/powerup-lady-bug-pick.mp3", import.meta.url).href;
const blackDespawn = new URL("../../assets/audio/black-despawn.wav", import.meta.url).href;
const levelUp2 = new URL("../../assets/audio/levelup2.mp3", import.meta.url).href;
const petolectroMenu = new URL("../../assets/audio/petolectro-menu.mp3", import.meta.url).href;
const summer = new URL("../../assets/audio/summer.mp3", import.meta.url).href;
const summerstreaming = new URL("../../assets/audio/summerstreaming.mp3", import.meta.url).href;
const gameOverShort = new URL("../../assets/audio/gameover-short.mp3", import.meta.url).href;
const balloon = new URL("../../assets/audio/balloon.mp3", import.meta.url).href;
const success = new URL("../../assets/audio/success.mp3", import.meta.url).href;

/** The original's `SoundEffects` field names, minus the `List<>` plumbing. */
export type SfxBankName =
  | "BugSpawn"
  | "BugDies"
  | "LadyBugTakenByPlayer"
  | "FatBugDies"
  | "BugInfects"
  | "BlockBuildsDown"
  | "BlockBuildsUp"
  | "SubBlockBuildsDown"
  | "PowerUpSpawns"
  | "PowerUpKilledByBug"
  | "PowerUpTakenByPlayer"
  | "CubeRotates"
  | "FatBugHit"
  | "SpawnPointDies"
  | "DiamondsPick"
  | "BlackBuggSpawn"
  | "BlackBuggPickedByPlayer"
  | "BlackBuggDisappearsByItself";

/**
 * Bank -> clips, exactly as bound in `MainGame.unity`. A play picks uniformly
 * at random (`Cube.GetAnyOf`); an **empty bank is silent by design** —
 * `GetAnyOf` returns null for it and `PlayOneShot` null-guards
 * (`SubBlockBuildsDown` at the eat-through, `SpawnPointDies` at the apple
 * cull). `DiamondsPick` is bound as powerup_lady_bug_pick ×5 + Test_7.wav,
 * indexed `comboLength - 2` for lengths 2-5 — every *reachable* index is the
 * same clip, and Test_7.wav sits at index 5, unreachable behind the
 * `combo.Count <= 5` gate (`Cube.cs:2135-2140`); it is collapsed to the one
 * clip here and Test_7.wav is not shipped.
 */
export const SFX_BANKS: Record<SfxBankName, readonly string[]> = {
  BugSpawn: [bugSpawn1, bugSpawn2, bugSpawn3],
  BugDies: [bugDie2, bugDie3, bugDie4, bugDie5],
  LadyBugTakenByPlayer: [powerUpPickPlayer],
  FatBugDies: [bugDie1],
  BugInfects: [bugInfect7],
  BlockBuildsDown: [blockDown],
  BlockBuildsUp: [blockUp],
  SubBlockBuildsDown: [],
  PowerUpSpawns: [powerUpSpawn],
  PowerUpKilledByBug: [powerUpBugKill2, powerUpBugKill],
  PowerUpTakenByPlayer: [powerUpPickPlayer],
  CubeRotates: [cubeRotates1, cubeRotates2, cubeRotates3],
  FatBugHit: [bugDie6],
  SpawnPointDies: [],
  DiamondsPick: [ladyBugPick],
  BlackBuggSpawn: [bugSpawn3],
  BlackBuggPickedByPlayer: [bugInfect6],
  BlackBuggDisappearsByItself: [blackDespawn],
};

/**
 * A jukebox play command: which of the two music channels, what clip at what
 * volume, looped or once. Channel 0 is *the* music channel — a command on it
 * replaces whatever channel 0 plays; channel 1 is the level-up stinger
 * overlay (`JukeBox.PlayOnJukeBox` keys `currentlyPlaying` by
 * `AudioSourceNumber`).
 */
export interface MusicTrack {
  readonly name: MusicTrackName;
  readonly url: string;
  /** Per-clip volume from the JukeBox `Volumes` table. */
  readonly volume: number;
  readonly loop: boolean;
  readonly channel: 0 | 1;
}

export type MusicTrackName =
  | "menu"
  | "inGame"
  | "inGameSecondHalf"
  | "levelUp"
  | "gameOver"
  | "highScore"
  | "goldTrophy"
  | "silverTrophy";

/**
 * The play commands, named as the `Cube` fields that hold them. `menu` is the
 * menu scene's `InGameMusic` (clip 5, Summer, once — plays through and then
 * silence, `MainMenu.unity:8552`); the rest are `MainGame.unity:3933-3960`
 * with clip numbers resolved through the clip/volume tables.
 * `silverTrophy` and `gameOver` are the same clip 8 on purpose.
 */
export const MUSIC: Record<MusicTrackName, MusicTrack> = {
  menu: { name: "menu", url: summer, volume: 0.7, loop: false, channel: 0 },
  inGame: { name: "inGame", url: balloon, volume: 0.7, loop: true, channel: 0 },
  inGameSecondHalf: { name: "inGameSecondHalf", url: summerstreaming, volume: 0.7, loop: true, channel: 0 },
  levelUp: { name: "levelUp", url: levelUp2, volume: 1, loop: false, channel: 1 },
  gameOver: { name: "gameOver", url: gameOverShort, volume: 1, loop: false, channel: 0 },
  highScore: { name: "highScore", url: petolectroMenu, volume: 0.7, loop: true, channel: 0 },
  goldTrophy: { name: "goldTrophy", url: success, volume: 1, loop: false, channel: 0 },
  silverTrophy: { name: "silverTrophy", url: gameOverShort, volume: 1, loop: false, channel: 0 },
};

/** Every distinct audio URL — what the engine warms its fetch cache with. */
export const ALL_AUDIO_URLS: readonly string[] = [
  ...new Set([
    ...Object.values(SFX_BANKS).flat(),
    ...Object.values(MUSIC).map((t) => t.url),
  ]),
];
